import React from "react";
import {
    MapPin,
    AtSign,
    ShieldCheck,
    Send,
    History,
    FileSearch,
    Upload,
    GitMerge,
    Activity,
    MessageCircle,
    ArrowRight,
} from "lucide-react";

const features = [
    {
        id: "F01",
        icon: MapPin,
        title: "Google Places lead search",
        body: "Search by location and multiple business types in one query. Continue saved runs with Load More when more data is available.",
    },
    {
        id: "F02",
        icon: AtSign,
        title: "Website email discovery",
        body: "Visit business websites, find public emails, ignore junk addresses, and remove duplicates as the workflow runs.",
    },
    {
        id: "F03",
        icon: ShieldCheck,
        title: "Email trust & quality scoring",
        body: "Score every discovered address by trust level. Filter out generic mailboxes, malformed entries, and weak signals before sending.",
    },
    {
        id: "F04",
        icon: GitMerge,
        title: "Duplicate consolidation",
        body: "Merge emails, websites, phones, and businesses by domain. Keep sent-history consistent across multi-branch companies.",
    },
    {
        id: "F05",
        icon: Upload,
        title: "File import (CSV / Excel)",
        body: "Import existing email, website, and phone lists. Auto-normalise numbers, infer websites from email domains, and save into Saved Searches.",
    },
    {
        id: "F06",
        icon: FileSearch,
        title: "Contact page helper",
        body: "Detect contact pages, launch a browser helper, and pre-fill forms using your saved templates — with quick-copy fallbacks.",
    },
    {
        id: "F07",
        icon: Send,
        title: "Rich editor + your own SMTP",
        body: "Compose with templates, send a test first, then ship through Google Workspace, Resend, or any SMTP you control. Provider-aware quota guard included.",
    },
    {
        id: "F08",
        icon: Activity,
        title: "Activity timeline per business",
        body: "A lightweight CRM trail: search events, enrichment, sends, manual notes, form outcomes, and WhatsApp outcomes — all stamped and editable.",
    },
    {
        id: "F09",
        icon: ShieldCheck,
        title: "Compliance & suppression",
        body: "Local and remote suppression, Cloudflare-backed unsubscribe handling, privacy notice generator, bounce/complaint visibility.",
    },
];

export default function Features() {
    return (
        <section
            id="features"
            data-testid="features-section"
            className="border-b border-border bg-surface"
        >
            <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                    <div>
                        <span className="overline">Main functions</span>
                        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 leading-[1.05] text-ink max-w-2xl">
                            Nine modules. One pragmatic workflow.
                        </h2>
                    </div>
                    <p className="text-ink-soft max-w-md">
                        From a market idea to a structured lead list with
                        contact routes, outreach history, and follow-up signals
                        — without juggling tabs and spreadsheets.
                    </p>
                </div>

                <div className="grid grid-skeleton md:grid-cols-2 lg:grid-cols-3 border border-border">
                    {features.map((f) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={f.id}
                                data-testid={`feature-${f.id}`}
                                className="bg-bg p-8 md:p-10 group hover:bg-surface-2 transition-colors duration-200 relative"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="h-10 w-10 border border-ink flex items-center justify-center group-hover:bg-ink group-hover:text-bg transition-colors">
                                        <Icon size={18} strokeWidth={2.25} />
                                    </div>
                                    <span className="overline">{f.id}</span>
                                </div>
                                <h3 className="font-display text-xl md:text-2xl font-semibold mt-6 text-ink">
                                    {f.title}
                                </h3>
                                <p className="mt-3 text-ink-soft leading-relaxed">
                                    {f.body}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* WhatsApp callout — distinctive differentiator */}
                <div
                    data-testid="whatsapp-callout"
                    className="mt-px border border-border bg-ink text-bg grid lg:grid-cols-12 gap-0"
                >
                    <div className="lg:col-span-7 p-8 md:p-12 lg:p-14 border-b lg:border-b-0 lg:border-r border-bg/15">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 border border-bg/30 flex items-center justify-center text-forge">
                                <MessageCircle size={18} strokeWidth={2.25} />
                            </div>
                            <span
                                className="font-mono uppercase tracking-[0.2em] text-[11px]"
                                style={{ color: "hsl(var(--ink-mute))" }}
                            >
                                Bonus module · WhatsApp handoff
                            </span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mt-5 leading-tight text-bg max-w-2xl">
                            Phone follow-up, without leaving the workspace.
                        </h3>
                        <p className="mt-4 text-bg/70 max-w-xl leading-relaxed">
                            Push likely mobile numbers to WhatsApp Web or
                            Desktop with prefilled message text from your
                            template. Pacing controls, configurable cooldowns,
                            and outcomes like <em>opened</em>, <em>replied</em>,
                            <em> do-not-contact</em> roll up into the same
                            activity timeline as your emails.
                        </p>
                    </div>
                    <div className="lg:col-span-5 p-8 md:p-12 lg:p-14 flex flex-col justify-between gap-8">
                        <ul className="space-y-3 text-sm text-bg/80">
                            {[
                                "Auto-detects mobile vs landline",
                                "Pulls message text from your template",
                                "Send delay · max-per-minute · cooldown",
                                "Outcomes shown in the Form Status column",
                            ].map((b) => (
                                <li
                                    key={b}
                                    className="flex items-start gap-3"
                                >
                                    <span className="h-1.5 w-1.5 bg-forge mt-2 shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <a
                            data-testid="whatsapp-callout-link"
                            href="#showcase"
                            className="inline-flex items-center gap-2 font-semibold text-sm text-bg link-underline self-start"
                        >
                            See it in the workspace{" "}
                            <ArrowRight size={14} className="text-forge" />
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
